import React from 'react';
import {connect} from 'react-redux';
import {FormattedMessage} from 'react-intl';
import {Link, useHistory} from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import qs from 'qs';
import {requestDashboard, requestDashboardExport} from '../../actions/action_dashboard';
import {requestExportSurvey} from '../../actions/action_survey_list';
import messages from '../../../res/translations/messages';
import Icon from '../Icon';
import Loader from '../Loader';
import Title from '../Title';
import WidgetContainer from './WidgetContainer';
import TooltipIcon from '../TooltipIcon';
import DashboardActions from './DashboardActions';
import DatePicker from 'react-datepicker';
import ReactDatePickerInput from '../quicksightDashboards/components/ReactDatePickerInput';
import {addMinutes} from 'date-fns';
import get from 'lodash/get';
import SurveyDownloadButton from '../survey/SurveyDownloadButton';
import InstanceFilters from './InstanceFilters';
import {INSTANCE_FILTERS} from '../../constants';

const dayUTC = (date) => date? addMinutes(date, -date.getTimezoneOffset()): null;
const dayLocal = (date) => date? addMinutes(date, date.getTimezoneOffset()): null;

const Dashboard = ({analytics, error, item, loading, match, requestDashboard,
    requestDashboardExport, requestExportSurvey, exportingDashboard, exportingSurvey}) => {
    const history = useHistory();
    const [hints, setHints] = React.useState({absolute: false, printable: false});
    const toggleAbsolute = React.useCallback(() => setHints(Object.assign({}, hints, {absolute: !hints.absolute})), [hints]);
    const actions = (
        <DashboardActions hints={hints} toggleAbsolute={toggleAbsolute} />
    );

    const [startDate, setStartDate] = React.useState(null);
    const setStartDateCall = React.useCallback((date) => setStartDate(dayUTC(date)), [setStartDate]);
    const [endDate, setEndDate] = React.useState(null);
    const setEndDateCall = React.useCallback((date) => setEndDate(dayUTC(date)), [setEndDate]);
    const [instanceFilter, setInstanceFilter] = React.useState(INSTANCE_FILTERS.COMPLETED);
    const setInstanceFilterCall = React.useCallback((value) => setInstanceFilter(value), [setInstanceFilter]);
    const [reflectedInstanceFilter, setReflectedInstanceFilter] = React.useState(INSTANCE_FILTERS.COMPLETED);
    const printParams = qs.stringify({
        absolute: hints.absolute || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        instanceType: instanceFilter,
    }, {addQueryPrefix: true});

    React.useEffect(() => {
        requestDashboard(match.params.id);
    }, [match, requestDashboard]);

    const handleApplyFilter = React.useCallback(() => {
        setReflectedInstanceFilter(`${instanceFilter}`);
        requestDashboard(match.params.id, startDate, endDate, instanceFilter);
    }, [match, requestDashboard, startDate, endDate, instanceFilter, reflectedInstanceFilter]);

    const handleClearFilters = React.useCallback(() => {
        startDate && setStartDate(null);
        endDate && setEndDate(null);
        instanceFilter !== INSTANCE_FILTERS.COMPLETED && setInstanceFilter(INSTANCE_FILTERS.COMPLETED);
        requestDashboard(match.params.id);
    }, [match, requestDashboard, startDate, endDate, instanceFilter]);

    const handleExport = React.useCallback(() => {
        const startDateParam = startDate && startDate.getTime() || '-';
        const endDateParam = endDate && endDate.getTime() || '-';
        requestDashboardExport(match.params.id, {startDate: startDateParam, endDate: endDateParam, instanceType: instanceFilter});
    }, [match, requestDashboardExport, startDate, endDate, instanceFilter]);

    const handleDownload = React.useCallback((params) => {
        const startDateParam = startDate && startDate.getTime() || '-';
        const endDateParam = endDate && endDate.getTime() || '-';
        requestExportSurvey(match.params.id, {...params, startDate: startDateParam, endDate: endDateParam});
    }, [match, requestExportSurvey, startDate, endDate]);

    const goBack = React.useCallback(() => {
        history.goBack();
    }, [history]);

    // keep local state for survey stats so we can trigger re-render when widgets update them
    const [surveyCompleted, setSurveyCompleted] = React.useState(0);
    const [surveyStarted, setSurveyStarted] = React.useState(0);

    // this is a trick to retrieve current stats for filtered view from widgets
    const updateSurveyStatsFromWidget = React.useCallback((completed, started) => {
        setSurveyCompleted(completed || 0);
        setSurveyStarted(started || 0);
    }, [setSurveyCompleted, setSurveyStarted]);

    return (
        <div className="container-fluid dashboard">
            <div className="row">
                <div className="col-md-2 col-12">
                    <div className="navbar-brand">
                        <button type="button" className="btn btn-outline-primary btn-sm" onClick={goBack}>
                            <Icon icon="arrow_left" /> <FormattedMessage {...messages.buttons.back} />
                        </button>
                    </div>
                </div>
                <div className="col-md-8 col-12">
                    <Title actions={actions}><FormattedMessage {...messages.dashboard.title} values={{name: item && item.name}} /></Title>
                </div>
                <div className="col-md-2 col-12 text-right">
                    <div className="d-flex flex-column">
                        <Link to={`/dashboard/${match.params.id}/print${printParams}`} target="_blank"
                            className={`btn btn-outline-primary btn-sm mb-1 ${(surveyCompleted == 0 && surveyStarted == 0)? 'disabled':''}`}>
                            <FontAwesomeIcon icon={['far', 'file-pdf']} className="mr-1" />
                            <FormattedMessage {...messages.dashboard.buttons.print} />&nbsp;
                            <TooltipIcon message={messages.dashboard.print.buttonTooltip} />
                        </Link>
                        <a onClick={handleExport}
                            className={`btn btn-outline-primary btn-sm ${exportingDashboard || (surveyCompleted == 0 && surveyStarted == 0)? 'disabled':''}`} >
                            {exportingDashboard ?
                                <FontAwesomeIcon icon="circle-notch" spin />:
                                <FontAwesomeIcon icon={['far', 'file-excel']} />
                            } <FormattedMessage {...messages.dashboard.buttons.exportXlsx} />
                        </a>
                        <SurveyDownloadButton item={item}
                            showEmptyWarning={!(surveyCompleted) && !(surveyStarted)} instanceType={instanceFilter}
                            surveyCompleted={surveyCompleted} surveyStarted={surveyStarted}
                            disabled={(!surveyStarted && exportingSurvey)} onExport={handleDownload}
                            className={`btn btn-outline-primary btn-sm mt-1 ${(surveyCompleted == 0 && surveyStarted == 0)? 'disabled':''}`}>
                            <span>
                                {exportingSurvey ?
                                    <FontAwesomeIcon icon="circle-notch" spin />:
                                    <FontAwesomeIcon icon="file-download" fixedWidth={true} />
                                } <FormattedMessage {...messages.dashboard.buttons.downloadDataset} />
                            </span>
                        </SurveyDownloadButton>
                    </div>
                </div>
            </div>
            <div className="row mb20">
                <div className="col-md-2 col-12" />
                <div className="col-md-2 col-12 mt5">
                    <FormattedMessage {...messages.dashboard.labels.from} />:
                    <div className="datepicker-wrapper">
                        <DatePicker
                            selected={dayLocal(startDate)}
                            onChange={setStartDateCall}
                            selectsStart
                            startDate={dayLocal(startDate)}
                            endDate={dayLocal(endDate)}
                            customInput={<ReactDatePickerInput />}
                            isClearable
                            dateFormat="dd/MM/yyyy"
                        />
                    </div>
                </div>
                <div className="col-md-2 col-12 mt5">
                    <FormattedMessage {...messages.dashboard.labels.to} />:
                    <div className="datepicker-wrapper">
                        <DatePicker
                            selected={dayLocal(endDate)}
                            onChange={setEndDateCall}
                            selectsEnd
                            startDate={dayLocal(startDate)}
                            endDate={dayLocal(endDate)}
                            minDate={dayLocal(startDate)}
                            customInput={<ReactDatePickerInput />}
                            isClearable
                            dateFormat="dd/MM/yyyy"
                        />
                    </div>
                </div>
                <div className="col-md-2 col-12 mt5">
                    <FormattedMessage {...messages.dashboard.labels.instances} />:<br />
                    <InstanceFilters value={instanceFilter} onChange={setInstanceFilterCall} />
                </div>
                <div className="col-md-1 col-12 mt5 text-right" style={{marginLeft: '20px'}}>
                    &nbsp;
                    <div className="d-flex flex-column">
                        <button className="btn btn-primary" type="button" onClick={handleApplyFilter}>
                            <FormattedMessage {...messages.dashboard.buttons.apply} />
                        </button>
                    </div>
                </div>
                <div className="col-md-1 col-12 mt5 text-right">
                    &nbsp;
                    <div className="d-flex flex-column">
                        {(instanceFilter != INSTANCE_FILTERS.COMPLETED || startDate || endDate) && (
                            <button type="button" className="btn btn-link mb-1" onClick={handleClearFilters}>
                                <FormattedMessage {...messages.surveys.list.reset} />
                            </button>
                        )}
                    </div>
                </div>
                <div className="col-md-2 col-12 mt5" />
            </div>
            {error && <div className="alert alert-danger">Cannot load dashboard</div>}
            {loading && <Loader />}
            {!loading && item && <WidgetContainer analytics={analytics} widgets={item.widgets} hints={hints} startDate={startDate}
                endDate={endDate} instanceType={reflectedInstanceFilter} updateSurveyStatsFromWidget={updateSurveyStatsFromWidget} />}
        </div>
    );
};

const mapStateToProps = (state) => (
    {
        analytics: state.analytics.data,
        error: state.dashboard.error,
        item: state.dashboard.item,
        loading: state.dashboard.loading,
        exportingDashboard: state.dashboard.item? get(state, `exports.dashboards.${state.dashboard.item.id}.exporting`, false): false,
        exportingSurvey: state.dashboard.item? get(state, `exports.items.${state.dashboard.item.id}.exporting`, false): false,
    }
);

export default connect(mapStateToProps, {requestDashboard, requestDashboardExport, requestExportSurvey})(Dashboard);
